import { useMutation } from '@apollo/client';
import { Button, Form, Input, notification, Typography } from 'antd';
import React from 'react';
import { useHistory, useParams } from 'react-router-dom';

import RESET_PASSWORD from '../../apollo/auth/mutations/resetPassword';
import { ErrorLogger } from '../../utils/ErrorLogger';
import { messages, MessageTypes } from './messages';

const { Password } = Input;
const { Title } = Typography;

export interface ResetForm {
  password: string;
  confirm: string;
}

const ResetPassword: React.FC = () => {
  const history = useHistory();
  const { code } = useParams<{ code: string }>();

  const [resetPassword, { loading }] = useMutation(RESET_PASSWORD);

  const onFinish = async ({ password }: ResetForm) => {
    const data = {
      password,
      passwordResetCode: code,
    };

    try {
      await resetPassword({ variables: { data } });
      notification.success({
        message:
          'Your password has been successfully updated. Please login using the new data.',
      });
      history.replace('/auth/login');
    } catch (err) {
      ErrorLogger.logError(err);
    }
  };

  return (
    <Form
      className="app__form app__form--auto app__form--center app__form--space-between"
      onFinish={onFinish}
    >
      <Form.Item>
        <Title level={4}>Update your Password</Title>
      </Form.Item>
      <Form.Item
        name="password"
        rules={[
          {
            min: 8,
            required: true,
            message: messages[MessageTypes.RequiredField],
          },
        ]}
        hasFeedback
      >
        <Password size="large" placeholder="New Password" />
      </Form.Item>

      <Form.Item
        name="confirm"
        dependencies={['password']}
        hasFeedback
        rules={[
          {
            required: true,
            message: messages[MessageTypes.RequiredField],
          },
          ({ getFieldValue }) => ({
            validator(rule, value) {
              if (!value || getFieldValue('password') === value) {
                return Promise.resolve();
              }

              return Promise.reject(
                messages[MessageTypes.IncomparablePassword]
              );
            },
          }),
        ]}
      >
        <Password size="large" placeholder="Repeat New Password" />
      </Form.Item>

      <Form.Item>
        <Button
          size="large"
          type="primary"
          htmlType="submit"
          className="login-form-button"
          loading={loading}
        >
          Send
        </Button>
      </Form.Item>
    </Form>
  );
};

export default ResetPassword;
